import { useState } from "react";
import { Mail, X, Loader2, Send } from "lucide-react";
import { toast } from "sonner";

/**
 * Modal reutilizable para "Enviar por email" cualquier informe (PDF/XLSX)
 * sin tener que descargarlo primero. `onSend` debe devolver una promesa que
 * llama al endpoint backend correspondiente con { to, message }.
 */
export default function SendEmailModal({ open, onClose, onSend, title, attachmentLabel }) {
  const [to, setTo] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  if (!open) return null;

  const handleSend = async () => {
    const emails = to.split(",").map(e => e.trim()).filter(Boolean);
    if (emails.length === 0) {
      toast.error("Indica al menos un email destinatario");
      return;
    }
    const invalid = emails.find(e => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e));
    if (invalid) {
      toast.error(`Email no válido: ${invalid}`);
      return;
    }
    setSending(true);
    try {
      await onSend({ to: emails.join(","), message: message.trim() || undefined });
      toast.success(`Enviado a ${emails.join(", ")}`);
      setTo(""); setMessage("");
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo enviar el email");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/60 p-4"
      data-testid="send-email-modal" onClick={() => !sending && onClose()}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <h3 className="text-base font-bold text-slate-800 flex items-center gap-2">
            <Mail size={17} className="text-[#007934]" /> {title || "Enviar por email"}
          </h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-100 text-slate-500">
            <X size={18} />
          </button>
        </div>
        <div className="p-5 space-y-3 text-sm">
          {attachmentLabel && (
            <p className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
              Adjunto: <strong>{attachmentLabel}</strong>
            </p>
          )}
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Destinatario(s)</label>
            <input
              data-testid="send-email-to-input"
              type="text"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="correo1@valorizasm.com, correo2@valorizasm.com"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
              autoFocus
            />
            <p className="text-[11px] text-slate-400 mt-1">Separa varios emails con comas</p>
          </div>
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Mensaje (opcional)</label>
            <textarea
              data-testid="send-email-message-input"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
              placeholder="Añade un mensaje..."
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm resize-none"
            />
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-200 bg-slate-50">
          <button onClick={onClose} disabled={sending}
            className="px-3 py-1.5 rounded-lg text-sm font-semibold bg-white border border-slate-300 text-slate-700 hover:bg-slate-100">
            Cancelar
          </button>
          <button data-testid="send-email-submit-btn" onClick={handleSend} disabled={sending}
            className="inline-flex items-center gap-1 px-4 py-1.5 rounded-lg text-sm font-semibold bg-[#007934] text-white hover:bg-[#005c27] disabled:opacity-50">
            {sending ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
            Enviar
          </button>
        </div>
      </div>
    </div>
  );
}
